import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '~/lib/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'accent' | 'outline';
type Size = 'sm' | 'md' | 'lg' | 'icon';

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

const variants: Record<Variant, string> = {
  primary:
    'bg-primary text-primary-foreground hover:opacity-90 active:opacity-80 shadow-sm',
  secondary:
    'bg-muted text-foreground hover:bg-[color-mix(in_srgb,var(--color-muted)_85%,var(--color-foreground))] border border-border',
  ghost:
    'bg-transparent text-foreground hover:bg-muted',
  outline:
    'bg-transparent text-foreground border border-border hover:bg-muted',
  destructive:
    'bg-destructive text-destructive-foreground hover:opacity-90',
  accent:
    'bg-accent text-accent-foreground hover:opacity-90',
};

const sizes: Record<Size, string> = {
  sm: 'h-8 px-3 text-[13px]',
  md: 'h-10 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
  icon: 'h-10 w-10',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { className, variant = 'primary', size = 'md', leftIcon, rightIcon, children, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-[var(--radius)] font-medium',
        'transition-[opacity,background-color,transform] duration-150',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        'disabled:cursor-not-allowed disabled:opacity-50',
        variants[variant],
        sizes[size],
        className,
      )}
      {...rest}
    >
      {leftIcon && <span className="-ml-0.5 inline-flex items-center">{leftIcon}</span>}
      {children}
      {rightIcon && <span className="-mr-0.5 inline-flex items-center">{rightIcon}</span>}
    </button>
  );
});
